import { Pagination } from '@/components/ui/pagination'
import { SearchInput } from '@/components/ui/search-input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useTableSearch } from '@/hooks/useTableSearch'
import { Demo, DsSection } from '../primitives'

interface DemoRow {
  vin: string
  make: string
  model: string
}

const MAKES = ['Freightliner', 'Peterbilt', 'Kenworth', 'Volvo', 'Mack']
const MODELS = ['Cascadia', '579', 'T680', 'VNL 760', 'Anthem']

const ROWS: DemoRow[] = Array.from({ length: 120 }, (_, index) => ({
  vin: `1FUJGLDR${String(index).padStart(8, '0')}`,
  make: MAKES[index % MAKES.length],
  model: MODELS[index % MODELS.length],
}))

const rowSearchValues = (row: DemoRow) => [row.vin, row.make, row.model]

export function TableToolbarSection() {
  const { query, onQueryChange, page, setPage, totalPages, pageItems, filtered, rangeSummary } =
    useTableSearch({ items: ROWS, getSearchValues: rowSearchValues, pageSize: 8 })

  return (
    <DsSection
      id="table-search"
      title="Table search & client-side paging"
      intro={
        <>
          Long lists the client already holds in full (trucks, trailers, drivers) pair{' '}
          <code>&lt;SearchInput&gt;</code> above the table with <code>&lt;Pagination&gt;</code> below it. Both
          are driven by the <code>useTableSearch</code> hook, which filters the loaded list and slices it into
          numbered pages, and the pager hides itself when everything fits on one page. For a server-paginated
          table, where only "is there a next page" is known, use <code>&lt;TablePagination&gt;</code> from the
          Table pagination section instead.
        </>
      }
    >
      <Demo label="Search + paginated table" className="flex flex-col gap-4">
        <SearchInput
          value={query}
          onValueChange={onQueryChange}
          placeholder="Search trucks..."
          aria-label="Search trucks"
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">VIN</TableHead>
              <TableHead className="font-semibold">Make</TableHead>
              <TableHead className="font-semibold">Model</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-muted-foreground pt-5 text-center">
                  No trucks match “{query}”
                </TableCell>
              </TableRow>
            )}
            {pageItems.map((row) => (
              <TableRow key={row.vin}>
                <TableCell>{row.vin}</TableCell>
                <TableCell>{row.make}</TableCell>
                <TableCell>{row.model}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          summary={rangeSummary('trucks')}
        />
      </Demo>
    </DsSection>
  )
}
